import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, FileCheck2, Scale, ShieldCheck } from "lucide-react";
import { getCoverage } from "@/lib/api";
import { STORE } from "@/constants/testIds";

const formatDate = (value) => {
  if (!value) return "";
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch (error) {
    return value;
  }
};

const SummaryStat = ({ value, label }) => (
  <div className="border-l-4 border-l-[var(--ibp-gold-500)] bg-white px-5 py-4">
    <p className="text-2xl font-bold text-[var(--ibp-ink)]">{value}</p>
    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.55)]">
      {label}
    </p>
  </div>
);

const CoverageTable = ({ title, description, rows }) => (
  <section>
    <h2 className="text-xl font-bold text-[var(--ibp-ink)]">{title}</h2>
    <p className="mt-2 max-w-3xl text-sm leading-6 text-[color:rgba(11,18,32,0.68)]">
      {description}
    </p>
    <div className="mt-5 overflow-x-auto border-y border-[color:rgba(10,27,51,0.12)] bg-white">
      <table className="w-full min-w-[620px] text-left text-sm">
        <thead className="bg-[var(--ibp-offwhite)] text-xs uppercase tracking-wide text-[color:rgba(11,18,32,0.55)]">
          <tr>
            <th className="px-4 py-3">Official outline section</th>
            <th className="px-4 py-3 text-right">Published weight</th>
            <th className="px-4 py-3 text-right">Simulation questions</th>
            <th className="px-4 py-3 text-right">Questions in bank</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((row) => (
            <tr key={row.section} className="border-t border-[color:rgba(10,27,51,0.08)]">
              <td className="px-4 py-3 font-medium text-[var(--ibp-ink)]">{row.section}</td>
              <td className="px-4 py-3 text-right text-[color:rgba(11,18,32,0.68)]">
                {row.official_weight_pct}%
              </td>
              <td className="px-4 py-3 text-right font-semibold text-[var(--ibp-ink)]">
                {row.official_simulation_questions}
              </td>
              <td className="px-4 py-3 text-right font-semibold text-[var(--ibp-ink)]">
                {row.bank_questions}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

export default function Coverage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-coverage"],
    queryFn: getCoverage,
  });

  return (
    <div data-testid={STORE.coveragePage} className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-14 text-white sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#E7BE4A]">
            Coverage and currency
          </p>
          <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight sm:text-4xl">
            See exactly what the question bank covers.
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-white/75 sm:text-base">
            Transparent counts, current review dates, and the official outline used to organize
            Illinois Broker Prep.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-6xl space-y-14 px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
        {isLoading ? (
          <p className="text-sm text-[color:rgba(11,18,32,0.65)]">Loading coverage...</p>
        ) : isError || !data ? (
          <p className="text-sm text-[#9b2c2c]">Coverage information is temporarily unavailable.</p>
        ) : (
          <>
            <section>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <SummaryStat value={data.total_questions} label="Total practice questions" />
                <SummaryStat value={data.counts.state} label="Illinois-specific" />
                <SummaryStat value={data.counts.national} label="National concepts" />
                <SummaryStat value={data.counts.math} label="Math drills" />
              </div>
              <div className="mt-6 grid gap-5 md:grid-cols-3">
                <div className="flex gap-3">
                  <FileCheck2 className="mt-0.5 h-5 w-5 flex-none text-[#B7791F]" />
                  <div>
                    <h2 className="text-sm font-semibold text-[var(--ibp-ink)]">Bank review</h2>
                    <p className="mt-1 text-sm leading-6 text-[color:rgba(11,18,32,0.68)]">
                      Question records last reviewed {formatDate(data.bank_review_date)}.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Scale className="mt-0.5 h-5 w-5 flex-none text-[#B7791F]" />
                  <div>
                    <h2 className="text-sm font-semibold text-[var(--ibp-ink)]">Current outline</h2>
                    <p className="mt-1 text-sm leading-6 text-[color:rgba(11,18,32,0.68)]">
                      Organized around the state outline effective {formatDate(data.outline_effective_date)}.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 flex-none text-[#B7791F]" />
                  <div>
                    <h2 className="text-sm font-semibold text-[var(--ibp-ink)]">Source transparency</h2>
                    <p className="mt-1 text-sm leading-6 text-[color:rgba(11,18,32,0.68)]">
                      {data.quality.with_source_references} questions include source references and{" "}
                      {data.quality.with_explanations} include explanations.
                    </p>
                  </div>
                </div>
              </div>
              <a
                href={data.official_source_url}
                target="_blank"
                rel="noreferrer"
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F3D8A] hover:underline"
              >
                Read the official PSI candidate bulletin <ExternalLink className="h-4 w-4" />
              </a>
            </section>

            <CoverageTable
              title="Illinois state portion"
              description="The Illinois simulation uses 40 questions in 90 minutes and targets the current published section weights."
              rows={data.state_sections}
            />
            <CoverageTable
              title="National portion"
              description="The National simulation uses 100 questions in 150 minutes. Math is part of the published National Broker outline."
              rows={data.national_sections}
            />

            <p className="border-l-4 border-l-[var(--ibp-gold-500)] pl-4 text-xs leading-6 text-[color:rgba(11,18,32,0.62)]">
              Illinois Broker Prep is an independent study aid. PSI and IDFPR do not review,
              approve, or endorse this question bank. Counts describe our original practice
              material, not actual exam questions.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
